import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ExpertCard, PartnerCard, ProductCard } from "../components/Cards";
import { ArrowRight, Sparkles, Users, Printer, ShoppingBag, Wrench } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const [experts, setExperts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [efiles, setEfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/experts?limit=6"),
      api.get("/partners?limit=3"),
      api.get("/products?limit=8&sort=featured"),
      api.get("/products?category=e-file&limit=4"),
    ]).then(([e, p, f, ef]) => {
      setExperts(e.data); setPartners(p.data); setFeatured(f.data); setEfiles(ef.data);
      setLoadError(false);
    }).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, []);

  const cards = [
    { icon: Users, title: "Find a 3D Designer", desc: "CAD, Product Design, 3D Modeling and more.", cta: "Explore Designers", href: "/experts?category=Designer" },
    { icon: Printer, title: "Find a 3D Printer Owner", desc: "By technology, materials and location.", cta: "Find a Printer", href: "/experts/printing-partners" },
    { icon: ShoppingBag, title: "Explore 3D Products", desc: "Digital files and physical products.", cta: "Visit Shop", href: "/shop" },
    { icon: Wrench, title: "Buy Equipment", desc: "Printers, filaments, resins and more.", cta: "Shop Equipment", href: "/shop/printers" },
  ];

  return (
    <div data-testid="home-page">
      {loadError && <div data-testid="home-load-error" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5"><div className="tl-alert"><strong>Discovery is temporarily unavailable.</strong> Please refresh and try again.</div></div>}
      {/* HERO */}
      <section className="relative overflow-hidden tl-hero-grid">
        <div className="absolute inset-0 tl-grid-bg" />
        <div className="absolute inset-0" style={{background: "radial-gradient(circle at 70% 40%, rgba(6, 182, 212, 0.1), transparent 50%)"}} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 tl-fade-up">
              <div className="tl-label mb-6 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Premium 3D Platform</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                Create. Design.<br />
                <span className="bg-gradient-to-r from-[#0891B2] via-[#3B82F6] to-[#2563EB] bg-clip-text text-transparent">Print. Build.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#475569] max-w-xl leading-relaxed">
                Find the right expert, discover quality 3D products, and get everything you need to bring your ideas to life.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/experts" data-testid="hero-cta-primary" className="tl-btn-primary">Find an Expert <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/shop" data-testid="hero-cta-secondary" className="tl-btn-ghost">Explore Shop</Link>
              </div>
              <div className="mt-12 grid grid-cols-3 max-w-md divide-x divide-slate-200 border-y border-slate-200">
                <div className="py-4 pr-4"><div className="font-display text-2xl font-bold">500+</div><div className="text-xs text-[#475569] mt-0.5">Verified Experts</div></div>
                <div className="py-4 px-4"><div className="font-display text-2xl font-bold">120+</div><div className="text-xs text-[#475569] mt-0.5">3D Printer Owners</div></div>
                <div className="py-4 pl-4"><div className="font-display text-2xl font-bold">2K+</div><div className="text-xs text-[#475569] mt-0.5">Products & Files</div></div>
              </div>
            </div>
            <div className="lg:col-span-5 relative tl-fade-up tl-fade-up-d1">
              <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-[#2563EB]/20 via-transparent to-[#0891B2]/20 blur-2xl" />
              <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/[0.1] shadow-[0_24px_80px_rgba(0,0,0,0.6)]">
                <img src={HERO_IMG} alt="3D Printing" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#FFFFFF] via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 glass rounded-xl p-4">
                  <div className="tl-label mb-1">Live now</div>
                  <div className="text-sm font-semibold">42 experts creating right now</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discovery cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(({ icon: Icon, title, desc, cta, href }, i) => (
            <Link key={title} to={href} data-testid={`discovery-card-${title.split(" ").pop()}`} className={`tl-card p-6 group tl-fade-up ${i === 1 ? "tl-fade-up-d1" : i === 2 ? "tl-fade-up-d2" : i === 3 ? "tl-fade-up-d3" : ""}`}>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB]/20 to-[#0891B2]/15 border border-[#2563EB]/25 flex items-center justify-center mb-5 group-hover:border-[#0891B2]/50 transition-colors">
                <Icon className="w-5 h-5 text-[#0891B2]" strokeWidth={1.75} />
              </div>
              <h3 className="font-display font-semibold text-lg mb-2">{title}</h3>
              <p className="text-sm text-[#475569] mb-4 line-clamp-2">{desc}</p>
              <span className="text-xs text-[#0891B2] flex items-center gap-1 group-hover:gap-2 transition-all">{cta} <ArrowRight className="w-3 h-3" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Experts */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tl-label mb-2">Discover</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold">Find the Right Expert</h2>
          </div>
          <Link to="/experts" data-testid="view-all-experts" className="text-sm text-[#0891B2] hover:text-[#0891B2] flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {loading ? <div data-testid="home-experts-loading" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div> : experts.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{experts.map((e) => <ExpertCard key={e.id} expert={e} />)}</div> : <div data-testid="home-experts-empty" className="tl-empty">No experts are available yet.</div>}
      </section>

      {/* Partners */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tl-label mb-2">Manufacture</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold">Find a 3D Printer Owner</h2>
          </div>
          <Link to="/experts/printing-partners" data-testid="view-all-partners" className="text-sm text-[#0891B2] flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {loading ? <div data-testid="home-partners-loading" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div> : partners.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{partners.map((p) => <PartnerCard key={p.id} partner={p} />)}</div> : <div data-testid="home-partners-empty" className="tl-empty">3D printer owners will appear here soon.</div>}
      </section>

      {/* E-Files banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="tl-card p-8 lg:p-14 bg-gradient-to-br from-[#F1F5F9] via-[#F8FAFC] to-[#FFFFFF] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-[#2563EB]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="tl-label mb-3">E-Files</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold mb-3">Digital Designs for Your Next Project</h2>
              <p className="text-[#475569] mb-6 max-w-lg">Discover free and premium 3D files created by designers and creators on Trinity Layers.</p>
              <Link to="/shop/e-files" data-testid="explore-efiles-cta" className="tl-btn-primary">Explore E-Files <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {efiles.slice(0, 4).map((f) => (
                <Link key={f.id} to={`/shop/product/${f.slug}`} className="aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <img src={(f.images || [])[0]} alt={f.name} className="w-full h-full object-cover" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="tl-label mb-2">Shop</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold">Featured Products</h2>
          </div>
          <Link to="/shop" className="text-sm text-[#0891B2] flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}

function SkeletonCard() { return <div data-testid="home-content-skeleton" className="tl-card p-5 animate-pulse"><div className="h-16 w-16 rounded-full bg-slate-100 mb-5" /><div className="h-4 bg-slate-100 rounded w-2/3 mb-3" /><div className="h-3 bg-slate-100 rounded w-full mb-2" /><div className="h-3 bg-slate-100 rounded w-4/5" /></div>; }
